import { useCallback, useEffect, useRef, useState } from "react";
import type { ClientMessage, GameStateView, ServerMessage } from "@ludo/shared";
import { getInitData } from "./telegram";
import { API_BASE } from "./api";

export type ConnectionStatus = "connecting" | "connected" | "reconnecting" | "disconnected";

interface SocketState {
  status: ConnectionStatus;
  state: GameStateView | null;
  lastError: { code: string; message: string } | null;
}

/**
 * WebSocket hook with automatic reconnect and idempotent actions.
 * - First message after connect is JOIN_ROOM carrying Telegram initData
 *   (browser WebSocket cannot set custom headers).
 * - On reconnect we send our last known version; the server replies with
 *   RECONNECT_STATE (full state + any missed events).
 * - Every action carries a unique requestId (server-side idempotency).
 */
export function useGameSocket(roomCode: string | null) {
  const [sock, setSock] = useState<SocketState>({
    status: "disconnected",
    state: null,
    lastError: null,
  });
  const wsRef = useRef<WebSocket | null>(null);
  const versionRef = useRef<number>(0);
  const reconnectTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const attempts = useRef(0);
  const mounted = useRef(true);

  const connect = useCallback(() => {
    if (!roomCode) return;
    const initData = getInitData();
    if (!initData) return;

    setSock((s) => ({ ...s, status: attempts.current > 0 ? "reconnecting" : "connecting" }));

    const base = API_BASE;
    const proto = base.startsWith("https") ? "wss" : "ws";
    const host = base.replace(/^https?:\/\//, "");
    const url = `${proto}://${host}/api/rooms/${roomCode}/ws`;

    const ws = new WebSocket(url);
    wsRef.current = ws;

    ws.onopen = () => {
      const hello: ClientMessage = {
        type: "JOIN_ROOM",
        requestId: crypto.randomUUID(),
        initData,
        lastKnownVersion: versionRef.current,
      };
      ws.send(JSON.stringify(hello));
    };

    ws.onmessage = (ev) => {
      let msg: ServerMessage;
      try {
        msg = JSON.parse(ev.data as string) as ServerMessage;
      } catch {
        return;
      }
      if (msg.type === "ERROR") {
        setSock((s) => ({ ...s, lastError: { code: msg.code, message: msg.message } }));
        if (msg.code === "WEBSOCKET_AUTH_FAILED") ws.close();
        return;
      }
      if ("state" in msg && msg.state) {
        versionRef.current = msg.state.version;
        attempts.current = 0;
        setSock((s) => ({
          ...s,
          status: "connected",
          state: msg.state as GameStateView,
          lastError: null,
        }));
      }
    };

    ws.onclose = () => {
      if (!mounted.current) return;
      setSock((s) => ({ ...s, status: "reconnecting" }));
      const delay = Math.min(1000 * 2 ** attempts.current++, 10_000);
      reconnectTimer.current = setTimeout(connect, delay);
    };

    ws.onerror = () => ws.close();
  }, [roomCode]);

  useEffect(() => {
    mounted.current = true;
    connect();
    return () => {
      mounted.current = false;
      if (reconnectTimer.current) clearTimeout(reconnectTimer.current);
      wsRef.current?.close();
    };
  }, [connect]);

  const send = useCallback((msg: Omit<ClientMessage, "requestId" | "initData">) => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return false;
    const full = { ...msg, requestId: crypto.randomUUID() } as ClientMessage;
    ws.send(JSON.stringify(full));
    return true;
  }, []);

  return { ...sock, send, reconnect: connect };
}
